import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Dropdown, Nav, Navbar } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import {
  USER_TYPE,
  USER_TYPE_TO_DISPLAY_NAME,
} from '../../../../../constants/user';
import { getUserProfile, setPretendType } from '../../../../../redux/actions';
import { profileSelectors } from '../../../../../redux/selectors/profile';
import routes from '../../../../../routes/constants';
import text from '../../../../../text';
import { clearUser, getUser } from '../../../../../utilities/storage';
import {
  getUserType,
  isStaff,
  isUserAnonymous,
  isUserStaff,
} from '../../../../../utilities/user';
import FloatingMenu from '../../../../../mp/components/common/FloatingMenu';
import UserAvatar from '../../../../profile/UserAvatar';
import { useRedirect } from '../../../../../hooks/useRedirect';
import toUpper from 'lodash/toUpper';
import { alertConstants } from '../../../../../redux/constants';
import { useUpdateLoginEmailBannerFlagMutation } from '../../../../../redux/rtk';
/* eslint-disable react/prop-types */
const UserNav = ({ className }) => {
  const dispatch = useDispatch();
  const redirect = useRedirect();
  const userProfile = useSelector(profileSelectors.getUserProfile);
  const pretendType = useSelector(profileSelectors.getPretendType);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [updateLoginEmailBannerFlag] = useUpdateLoginEmailBannerFlagMutation();

  const userBadge = useMemo(
    () => userProfile && (pretendType ?? getUserType(userProfile)),
    [pretendType, userProfile]
  );

  const canShowPretendUserTypes = useMemo(
    () => userProfile && isStaff(userProfile.role),
    [userProfile]
  );

  const pretendUserTypes = useMemo(
    () =>
      canShowPretendUserTypes
        ? [
            USER_TYPE.FREE,
            USER_TYPE.ESSENTIALS,
            USER_TYPE.LARKI_SUPPORT,
          ].filter((type) => {
            if (pretendType && type === pretendType) return false;
            if (
              !pretendType &&
              type === USER_TYPE.LARKI_SUPPORT &&
              isUserStaff(userProfile)
            )
              return false;
            return true;
          })
        : [],
    [canShowPretendUserTypes, pretendType, userProfile]
  );

  useEffect(() => {
    // todo: why check for full name here?
    if (getUser() && userProfile.full_name === undefined) {
      dispatch(getUserProfile());
    }
  }, [dispatch, userProfile]);

  return (
    !isEmpty(userProfile) &&
    !isUserAnonymous(userProfile) && (
      <Nav className={cn('UserNav', { [className]: className })}>
        <Navbar.Collapse>
          <FloatingMenu
            isOpen={isMenuOpen}
            onOpenChange={setIsMenuOpen}
            placement='bottom-end'
            offset={{ mainAxis: 14, crossAxis: 0 }}
            arrowConfig={{
              fill: '#ffffff',
              stroke: '#dcdcdc',
              strokeWidth: 1,
              width: 18,
              height: 8,
            }}
            className='user-nav-menu-floating'
            innerClassName='user-nav-menu-floating-inner'
            containerSelector='.user-nav-menu-floating-inner'
            trigger={
              <button
                type='button'
                className='user-nav-trigger'
                aria-label={text('account')}
              >
                <div className='avatar-wrapper'>
                  <UserAvatar profileImage={userProfile.profile_image} />
                  {userBadge ? (
                    <Badge className={userBadge}>{text(userBadge)}</Badge>
                  ) : null}
                </div>
              </button>
            }
          >
            {({ onClose }) => (
              <>
                <div className='p-2 mb-1 user-name-container'>
                  <div
                    className='d-flex flex-column user-name-details'
                    onClick={() => {
                      onClose();
                      redirect.push(routes.user.profile);
                    }}
                  >
                    <p
                      className='mb-0 font-16 font-weight--600 text-truncate name'
                    >
                      {userProfile?.full_name}
                    </p>
                    <p className='mb-0 font-14 text-truncate email'>
                      {userProfile?.email}
                    </p>
                    <p className='title font-12 font-weight--400'>
                      {toUpper(text('account'))}
                    </p>
                  </div>
                </div>

                <Dropdown.Item
                  onClick={() => {
                    onClose();
                    redirect.push(routes.dashboard);
                  }}
                >
                  {text('dashboard')}
                </Dropdown.Item>

                <Dropdown.Item
                  href={routes.larkiWebsiteUrl.home}
                  target='_blank'
                  rel='noreferrer'
                  onClick={() => {
                    onClose();
                  }}
                >
                  {text('larkiURL')}
                </Dropdown.Item>

                {canShowPretendUserTypes ? (
                  <>
                    <Dropdown.Divider />

                    {pretendUserTypes.map((type) => (
                      <Dropdown.Item
                        key={type}
                        onClick={() => {
                          onClose();
                          dispatch(setPretendType(type));
                        }}
                      >
                        {text('viewAs', {
                          as: USER_TYPE_TO_DISPLAY_NAME[type],
                        })}
                      </Dropdown.Item>
                    ))}

                    <Dropdown.Divider />
                  </>
                ) : null}

                <Dropdown.Item
                  onClick={async () => {
                    await updateLoginEmailBannerFlag({
                      should_show_email_banner: false,
                    });
                    // clear login email banner
                    dispatch({ type: alertConstants.ALERT_RESET });

                    onClose();
                    // also clear it here in case we're on an external domain
                    clearUser();
                    redirect.push(routes.logout);
                  }}
                >
                  {text('logout')}
                </Dropdown.Item>
              </>
            )}
          </FloatingMenu>
        </Navbar.Collapse>
      </Nav>
    )
  );
};

export default UserNav;
